/**
 * Shared error handling for impersonation token expiry/revocation.
 *
 * When an impersonation token expires or is revoked (401), instead of logging
 * out the admin completely, we restore their original session.
 */
import { tokenService } from '../../services/tokenService';

// Type for the impersonation state (matches authSlice)
interface ImpersonationState {
  active: boolean;
  targetId: string;
  targetName: string;
  targetEmail: string;
  targetOrgId: string;
  targetRoles: string[];
  targetPermissions: string[];
  token: string;
  expiresAt: string;
  originalToken: string;
  originalUser: {
    id: string;
    email: string;
    organization_id: string;
    roles: string[];
    permissions: string[];
    given_name?: string;
    family_name?: string;
  } | null;
}

interface AuthState {
  impersonation: ImpersonationState | null;
}

interface RootState {
  auth: AuthState;
}

interface ApiWithDispatch {
  dispatch: (action: any) => void;
  getState: () => RootState;
}

/**
 * Handle auth errors during impersonation.
 *
 * If we're impersonating and get a 401 (token expired/revoked), restore the
 * admin's original session instead of clearing everything and forcing logout.
 *
 * @param api - RTK Query API object with dispatch and getState
 * @param resetApiCaches - Callback to reset API caches after restoration
 * @returns true if impersonation session was restored, false otherwise
 */
export function handleImpersonationAuthError(
  api: ApiWithDispatch,
  resetApiCaches: () => void
): boolean {
  const state = api.getState();
  const impersonation = state.auth.impersonation;

  // Check if we're currently impersonating with valid original credentials
  if (impersonation?.active && impersonation.originalToken && impersonation.originalUser) {
    console.warn('[Auth] Impersonation token expired/revoked, restoring admin session');

    // Restore the original admin session and clear impersonation state atomically.
    tokenService.clearRuntimeImpersonation({ restoreAdminSession: true });

    // Reset API caches to fetch fresh data as admin
    resetApiCaches();

    return true; // Impersonation was restored
  }

  return false; // Not impersonating, proceed with normal logout
}
